import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Search, Download, Image, Home, Heart, User, LogOut, LogIn, Settings } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { getFavoritesStats } from '../api/fancaps'
import { useAuth } from '../hooks/useAuth'

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, user, logout } = useAuth()

  // 获取收藏统计 - 只有认证用户才获取
  const { data: statsData } = useQuery({
    queryKey: ['favorites-stats'],
    queryFn: getFavoritesStats,
    refetchInterval: 30000, // 每30秒刷新一次
    staleTime: 15000, // 15秒内认为数据是新鲜的
    enabled: isAuthenticated, // 只有认证用户才调用API
  })

  const navItems = [
    { path: '/', label: '首页', icon: Home },
    { path: '/search', label: '搜索', icon: Search },
    { 
      path: '/favorites', 
      label: '我的收藏', 
      icon: Heart,
      badge: statsData?.data?.total_items || 0
    },
    // 开发环境才显示日志测试
    ...(process.env.NODE_ENV === 'development' ? [
      { path: '/operation-log-test', label: '日志测试', icon: Settings }
    ] : []),
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <Download className="h-8 w-8 text-primary-600" />
                <span className="text-xl font-bold text-gray-900">
                  Fancap下载器
                </span>
              </Link>
            </div>

            {/* 中间导航菜单 */}
            <nav className="hidden md:flex space-x-8">
              {navItems.map(({ path, label, icon: Icon, badge }) => (
                <Link
                  key={path}
                  to={path}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors relative ${
                    location.pathname === path
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${path === '/favorites' ? 'text-red-500' : ''}`} />
                  <span>{label}</span>
                  {/* 收藏统计徽章 */}
                  {path === '/favorites' && badge && badge > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </Link>
              ))}
            </nav>

            {/* 右侧认证区域 */}
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                /* 已登录状态 */
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2 text-sm text-gray-700">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {user?.email || '已登录'}
                    </span>
                  </div>
                  <button
                    onClick={logout}
                    className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                    title="登出"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="hidden sm:inline">登出</span>
                  </button>
                </div>
              ) : (
                /* 未登录状态 */
                <button
                  onClick={() => navigate('/login')}
                  className="flex items-center space-x-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                >
                  <LogIn className="h-4 w-4" />
                  <span>登录</span>
                </button>
              )}
            </div>

            {/* 移动端菜单按钮 */}
            <div className="md:hidden">
              <button className="p-2 rounded-md text-gray-400 hover:text-gray-500">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 主要内容区域 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </main>

      {/* 底部信息 */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-center text-sm text-gray-500">
            <p>
              Fancap下载器 - 高效的图片浏览和批量下载工具
              <span className="mx-2">•</span>
              版本 1.0.0
            </p>
            <p className="mt-1">
              所有图片版权归原作者和 Fancaps 所有，本工具仅供学习和个人非商业用途使用
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Layout